/**
 * Screen Reader Announcer Component
 * Provides ARIA live region for screen reader announcements
 */

import { useEffect, useRef } from 'react';
import { usePreferences } from '@/context/PreferencesContext';

interface ScreenReaderAnnouncerProps {
  message: string;
  priority?: 'polite' | 'assertive';
  clearAfter?: number;
}

export function ScreenReaderAnnouncer({
  message,
  priority = 'polite',
  clearAfter = 5000,
}: ScreenReaderAnnouncerProps) {
  const { preferences } = usePreferences();
  const timeoutRef = useRef<number>();

  useEffect(() => {
    if (clearAfter > 0) {
      timeoutRef.current = window.setTimeout(() => {
        // Message will be cleared by React when component unmounts
      }, clearAfter);
    }

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [message, clearAfter]);

  if (!preferences.screenReaderOptimized || !message) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live={priority}
      aria-atomic="true"
      className="sr-only"
    >
      {message}
    </div>
  );
}

/**
 * Hook for managing screen reader announcements
 */
export function useScreenReaderAnnouncement() {
  const announcementRef = useRef<HTMLDivElement>(null);

  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (announcementRef.current) {
      announcementRef.current.textContent = message;
      announcementRef.current.setAttribute('aria-live', priority);
    }
  };

  const AnnouncementRegion = () => (
    <div
      ref={announcementRef}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    />
  );

  return { announce, AnnouncementRegion };
}
